import {useQuery} from "@apollo/client";
import {useAccount, useMsal} from "@azure/msal-react";
import {Close} from "@bphxd/ds-core-react";
import {
  GET_RESERVE_MASTER_DETAILS,
  ReserveAPI,
} from "graphql/MassBalance/Actions/ReserveAPI";
import {uniqBy} from "lodash";
import {getAvailQtyandUom} from "modules/GlobalMassBalance/utils";
import PropTypes from "prop-types";
import {useAppSetting} from "providers/appSetting/context.js";
import {useEffect, useState} from "react";
import {FormProvider, useForm} from "react-hook-form";
import {toast} from "react-toastify";
import {
  Button,
  Form,
  FormFeedback,
  FormGroup,
  Input,
  Label,
  Modal,
  ModalBody,
  ModalFooter,
  ModalHeader,
  Spinner,
} from "reactstrap";
import {getDivisionData, getSiteDetails} from "utils/helpers/getAppSetting.js";
import {validateDecimalWithFivePoints} from "utils/numberUtil";
import "../../index.css";

const ReserveDetails = ({isVisibleModal, setModal, rowData, divisionCode}) => {
  const {appSetting} = useAppSetting();
  const countryId = appSetting?.currentCountryMappingData?.countryId;
  const siteReferenceData = getSiteDetails(countryId);
  const divisionData = getDivisionData(divisionCode);

  const {accounts} = useMsal();
  const account = useAccount(accounts[0]);
  const [isModalVisible, setModalVisible] = useState(isVisibleModal);
  const [isProcessing, setProcessing] = useState(false);
  useEffect(() => {
    setModalVisible(isVisibleModal);
  }, [isVisibleModal]);

  const methods = useForm({
    mode: "onChange",
    shouldFocusError: true,
  });

  const {
    register,
    formState: {errors},
    getValues,
    trigger,
    watch,
    setValue,
  } = methods;

  const {quantity, quantity_uom} = getAvailQtyandUom(rowData, divisionCode);
  const watchQuantity = parseFloat(watch("quantity"));

  const {data: masterDetails} = useQuery(GET_RESERVE_MASTER_DETAILS);

  const legalEntitiesItems =
    masterDetails?.bioLcGetBusinessAndLocationDetails
      ?.businessAndLocationDetails;

  const legalEntities = uniqBy(legalEntitiesItems ?? [], "businessPartnerCode");

  const onSubmit = async () => {
    const valid = await trigger();
    const {...docData} = getValues();
    if (valid) {
      setProcessing(true);
      const {data: response} = await ReserveAPI({
        customerName: docData.recipientName,
        mbInboundQtyEuId: rowData.original.inboundRecord.mbInboundQtyEuId,
        quantity: docData.quantity,
        createdBy: account.username,
        mbOutboundQtyEuId:
          rowData?.original?.outboundRecords?.mbOutboundQtyEuId || undefined,
        siteReferenceId: siteReferenceData?.siteReferenceId,
        divisionId: divisionData?.divisionId,
      });

      if (response?.bioLcGlobalMassBalanceReserve?.statusCode === 200) {
        setProcessing(false);
        setModalVisible(false);
        setModal("");
        toast.success(
          response?.bioLcGlobalMassBalanceReserve?.message ??
            "Successfully added reserve",
        );
      } else {
        setProcessing(false);
        toast.error(
          response?.bioLcGlobalMassBalanceReserve?.error ??
            "Something went wrong, failed to reserve",
        );
      }
    }
  };

  const computeProps = (name, options) => {
    const {ref, ...props} = register(name, options);
    return {innerRef: ref, ...props};
  };

  useEffect(() => {
    if (rowData?.original?.outboundRecords?.status === "RESERVED") {
      setValue("recipientName", rowData?.original?.outboundRecords?.recipient);
      setValue("quantity", rowData?.original?.outboundRecords?.quantityOutMt);
    }
  }, [
    rowData?.original?.outboundRecords?.quantityOutMt,
    rowData?.original?.outboundRecords?.status,
    rowData?.original?.outboundRecords?.recipient,
    setValue,
  ]);

  return (
    <div>
      <Modal
        size="sm"
        isOpen={isModalVisible}
        className="modal-dialog-centered mb-action-modal-344"
      >
        <ModalHeader
          className="!border-b-[1px] !border-gray-200 mb-5 text-xl"
          close={
            <Close
              onClick={() => {
                setModalVisible(false);
                setModal("");
              }}
            />
          }
        >
          Reserve product
        </ModalHeader>
        <ModalBody className="text-center pt-0">
          <FormProvider {...methods}>
            <Form className="flex flex-col gap-5 text-left">
              <FormGroup>
                <Label for="quantity" className="fw-light">
                  How much do you want to reserve?
                </Label>
                <Input
                  type="number"
                  id="quantity"
                  {...computeProps("quantity", {
                    required: "Please enter quantity",
                    pattern: validateDecimalWithFivePoints,
                  })}
                  invalid={!!errors.quantity}
                  maxLength={100}
                />
                {errors.quantity && (
                  <FormFeedback>{errors.quantity.message}</FormFeedback>
                )}
                {!errors.quantity && watchQuantity > quantity && (
                  <FormFeedback className="!block">
                    It cannot exceed the maximum quantity
                  </FormFeedback>
                )}
                <span className="text-xs text-gray-700">{`You have ${quantity} ${quantity_uom} available`}</span>
              </FormGroup>
              <FormGroup>
                <Label for="recipientName" className="fw-light">
                  Recipient name
                </Label>
                <Input
                  type="select"
                  id="recipientName"
                  data-test="recipientName"
                  invalid={!!errors.recipientName}
                  {...computeProps("recipientName", {
                    required: "Please select Recipient name",
                  })}
                >
                  <option key="none" value="">
                    --Select an option--
                  </option>
                  {legalEntities?.map((locdetails) => (
                    <option
                      key={locdetails.businessPartnerLongName}
                      value={locdetails.businessPartnerLongName}
                    >
                      {locdetails.businessPartnerLongName}
                    </option>
                  ))}
                </Input>
                {errors.recipientName && (
                  <FormFeedback>{errors.recipientName.message}</FormFeedback>
                )}
              </FormGroup>
            </Form>
          </FormProvider>
        </ModalBody>
        <ModalFooter className="p-0 d-block">
          <div className="row g-0 m-0 modal-footer-row">
            <div className="col-6 d-grid">
              <Button
                color="darker-tertiary"
                size="lg"
                className="border-0 rounded-0 py-4 opacity-80 opacity-100-hover bg-transparent"
                onClick={() => {
                  setModalVisible(false);
                  setModal("");
                }}
              >
                Cancel
              </Button>
            </div>
            <div className="col-6 d-grid">
              <Button
                color="darker-tertiary"
                size="lg"
                className="border-0 rounded-0 !p-0 bg-transparent text-default whitespace-nowrap"
                type="button"
                disabled={
                  isProcessing ||
                  errors.quantity ||
                  !watchQuantity ||
                  watchQuantity > quantity
                }
                onClick={onSubmit}
              >
                {isProcessing && (
                  <Spinner size="sm" className="btn-icon-prefix" />
                )}
                Reserve
              </Button>
            </div>
          </div>
        </ModalFooter>
      </Modal>
    </div>
  );
};

ReserveDetails.propTypes = {
  isVisibleModal: PropTypes.bool,
  setModal: PropTypes.func,
  rowData: PropTypes.object,
  divisionCode: PropTypes.string,
};

export default ReserveDetails;
